import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * GAVN custom (2026-09-02) — "Opgradér til hele sættet" i kurven.
 *
 * Erstatter enkeltvaren med sættet i ét /cart/update.js-kald (del → 0, sæt → 1)
 * og lader kurvens egne komponenter (cart-items-component, drawer, boble)
 * re-rendere via CartLinesUpdateEvent-promisen — samme mønster som
 * product-form.js og cart-discount.js.
 *
 * Markup: snippets/bundle-upsell.liquid.
 * SKAL GENINDSÆTTES EFTER EN TEMAOPDATERING (denne fil, snippet'en,
 * script-tag i scripts.liquid og placeringen i cart-summary.liquid).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [button]
 * @property {HTMLElement} [liveRegion]
 * @extends {Component<Refs>}
 */
class BundleUpsellComponent extends Component {
  #busy = false;

  /**
   * Bytter enkeltvaren ud med sættet.
   * @param {Event} [event]
   */
  async upgrade(event) {
    event?.preventDefault();
    if (this.#busy) return;

    const partVariant = this.dataset.partVariant;
    // Størrelse: har sættet en størrelsesvælger, bruges den valgte variant.
    // Ellers (sæt uden størrelse) bruges data-set-variant.
    const sizeSelect = this.refs.sizeSelect;
    const setVariant = sizeSelect ? sizeSelect.value : this.dataset.setVariant;

    if (!partVariant) return;

    // Kræv et størrelsesvalg — læg aldrig bare den første variant i.
    if (!setVariant) {
      this.classList.add('bundle-upsell--needs-size');
      if (this.refs.liveRegion) this.refs.liveRegion.textContent = 'Vælg en størrelse først.';
      sizeSelect?.focus();
      return;
    }

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    this.refs.button?.setAttribute('disabled', '');

    // Alle kurvsektioner på siden (drawer + evt. kurvside) skal refreshes.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter((id) => Boolean(id));

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: setVariant, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({
        updates: {
          [partVariant]: 0,
          [setVariant]: 1,
        },
        sections: sectionIds,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.status || !response.ok) {
        throw new Error(data.description || data.message || 'Kurven kunne ikke opdateres');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          items: data.items,
          source: 'bundle-upsell-component',
          sourceId: this.id ? this.id.toString() : undefined,
          sections: data.sections,
          didError: false,
        },
      });
      // Ved succes morfer kurvsektionen og denne komponent udskiftes/forsvinder,
      // så knappen behøver ikke gen-aktiveres.
    } catch (error) {
      deferred.reject(error);
      document.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Opgradering fejlede',
          code: 'UPGRADE_FAILED',
        })
      );
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent = 'Kunne ikke opgradere. Prøv igen.';
      }
      this.refs.button?.removeAttribute('disabled');
      this.removeAttribute('aria-busy');
      this.#busy = false;
    }
  }

  /** Fjerner "vælg størrelse"-markeringen når kunden vælger en størrelse. */
  clearSizeError() {
    this.classList.remove('bundle-upsell--needs-size');
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = '';
  }
}

if (!customElements.get('bundle-upsell-component')) {
  customElements.define('bundle-upsell-component', BundleUpsellComponent);
}
